import { toast, ToastHost } from "../../shared/toast";
import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import api from "../../core/api/api";
import { ADMIN_PATH } from "../../config/adminPath";
import useLiveSync from "../hooks/useLiveSync";


import {
  listOrders,
  getOrderStats,
  updateOrderStatus
} from "../services/orderService";

import { AdminCatalogProvider } from "../context/AdminCatalogContext";

// admin shell — eager (always visible / needed before the panel renders)
import AdminHeader from "../components/AdminHeader";
import AdminSidebar from "../components/AdminSidebar";
import AdminLogin from "./AdminLogin";

// jspdf is heavy (~250KB) → load the invoice util (and jspdf) only when an
// invoice is actually downloaded, so it stays OUT of the main bundle.
const downloadInvoice = (...args) =>
  import("../../utils/billing/invoice").then((m) => m.downloadInvoice(...args));

// admin pages — lazy: each becomes its own chunk, loaded only when visited.
// This shrinks the initial main bundle from ~1.3MB to a small shell.
const Dashboard              = lazy(() => import("./dashboard/Dashboard"));
const Purchase               = lazy(() => import("./purchase/Purchase"));
const PurchaseRegister       = lazy(() => import("./purchase/PurchaseRegister"));
const CustomersDashboard     = lazy(() => import("./customers/CustomersDashboard"));
const CustomerDetails        = lazy(() => import("./customers/CustomerDetails"));
const DeleteRequests         = lazy(() => import("./customers/DeleteRequests"));
const ProfileSchemaSettings  = lazy(() => import("./settings/ProfileSchemaSettings"));
const AccountSidebarSettings = lazy(() => import("./settings/AccountSidebarSettings"));
const NotificationSettings   = lazy(() => import("./settings/NotificationSettings"));
const AdminOrderDetail       = lazy(() => import("./customers/AdminOrderDetail"));
const HomeSections           = lazy(() => import("./home/HomeSections"));
const HomeBanners            = lazy(() => import("./home/HomeBanners"));
const Categories             = lazy(() => import("./catalog/Categories"));
const Brands                 = lazy(() => import("./catalog/Brands"));
const ProductsRoutes         = lazy(() => import("./products/ProductsRoutes"));
const InventoryCategory      = lazy(() => import("./inventory/InventoryCategory"));
const PincodeDashboard       = lazy(() => import("./delivery/PincodeDashboard"));
const PaymentSettings        = lazy(() => import("./settings/PaymentSettings"));
const Orders                 = lazy(() => import("./orders/Orders"));
const Billing                = lazy(() => import("./orders/Billing"));
const ProductAttributes      = lazy(() => import("./catalog/ProductAttributes"));
const ReviewsDashboard       = lazy(() => import("./reviews/ReviewsDashboard"));
const ReturnRequestsDashboard= lazy(() => import("./returns/ReturnRequestsDashboard"));
const SendNotification       = lazy(() => import("./notifications/SendNotification"));
const OffersDashboard        = lazy(() => import("./offers/OffersDashboard"));
const Announcements          = lazy(() => import("./marketing/Announcements"));
const Sellers                = lazy(() => import("./sellers/Sellers"));
const SellerDashboard        = lazy(() => import("./sellers/SellerDashboard"));
const SellerProducts         = lazy(() => import("./sellers/SellerProducts"));
const SellerPendingEdits     = lazy(() => import("./sellers/SellerPendingEdits"));
const SellerListings         = lazy(() => import("./sellers/SellerListings"));
const SellerPayouts          = lazy(() => import("./sellers/SellerPayouts"));
const MergeDuplicates        = lazy(() => import("./products/MergeDuplicates"));
const LicensesDashboard      = lazy(() => import("./licenses/LicensesDashboard"));
const NewLicense             = lazy(() => import("./licenses/NewLicense"));
const LicenseDetail          = lazy(() => import("./licenses/LicenseDetail"));

/* ── B2B Wholesale (separate module) ── */
const B2BDashboard   = lazy(() => import("./b2b/B2BDashboard"));
const B2BBuyers      = lazy(() => import("./b2b/B2BBuyers"));
const B2BBuyerDetail = lazy(() => import("./b2b/B2BBuyerDetail"));
const B2BNotifications = lazy(() => import("./b2b/B2BNotifications"));
const B2BHomeManage  = lazy(() => import("./b2b/B2BHomeManage"));
const B2BProducts    = lazy(() => import("./b2b/B2BProducts"));
const B2BOrders      = lazy(() => import("./b2b/B2BOrders"));
const B2BOrderDetail = lazy(() => import("./b2b/B2BOrderDetail"));

export default function Admin() {

  const navigate = useNavigate();
  const location = useLocation();
  const isB2B = location.pathname.includes("/b2b");

  /* ================= AUTH STATE ================= */
  const [adminLogged, setAdminLogged] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);

  /* ================= DATA ================= */
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState(null);   // accurate counts (beyond list cap)

  /* ================= CHECK ADMIN AUTH ================= */
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await api.get("/admin/me");
        if (res.data?.role === "admin") {
          setAdminLogged(true);
        } else {
          setAdminLogged(false);
        }
      } catch {
        setAdminLogged(false);
      } finally {
        setLoadingAuth(false);
      }
    };

    checkAdmin();
  }, []);

  /* ================= LOAD ORDERS (ONLY IF ADMIN) =================
     Single-source polling: initial load once, then the shared useLiveSync
     engine refreshes (pauses when the tab is hidden, refreshes on focus). */
  const loadOrders = useCallback(async () => {
    if (!adminLogged) return;
    try {
      const [list, stats] = await Promise.all([listOrders(), getOrderStats()]);
      setOrders(list || []);
      if (stats) setOrderStats(stats);
    } catch (err) {
      console.error("Failed loading orders", err.message);
    }
  }, [adminLogged]);

  useEffect(() => { if (adminLogged) loadOrders(); }, [adminLogged, loadOrders]);
  useLiveSync(loadOrders, { intervalMs: 30000, enabled: adminLogged });

  const handleUpdateOrderStatus = async (orderId, status, cancelReason = "") => {
    try {
      await updateOrderStatus(orderId, status, cancelReason);
      const list = await listOrders();
      setOrders(list || []);
    } catch (err) {
      toast(err.message || "Failed to update order status");
    }
  };

  const handleLogout = async () => {
    await api.post("/admin/logout");
    setAdminLogged(false);
    navigate(ADMIN_PATH);
  };

  /* ================= SHOW LOADING ================= */
  if (loadingAuth) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 22, margin: "0 auto 16px" }}>V</div>
          <div style={{ fontSize: 14, color: "#94a3b8", fontWeight: 600 }}>Loading VARAii Admin…</div>
        </div>
      </div>
    );
  }

  /* ================= LOGIN SCREEN ================= */
  if (!adminLogged) {
    return (
      <AdminLogin
        onLoggedIn={() => {
          setAdminLogged(true);
          navigate(`${ADMIN_PATH}/dashboard`);
        }}
      />
    );
  }

  /* ================= ADMIN PANEL ================= */
  return (
    <AdminCatalogProvider>
      <div style={{ minHeight: "100vh", background: isB2B ? "#bcc5d1" : "#f8fafc" }}>
        <ToastHost />
        <AdminHeader orders={orders} orderStats={orderStats} onLogout={handleLogout} />

        <div style={{ display: "flex" }}>
          <AdminSidebar />

          <div style={{ flex: 1, padding: 24, minHeight: "calc(100vh - 58px)", overflow: "auto" }}>
            <Suspense fallback={
              <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 14, fontWeight: 600 }}>
                Loading…
              </div>
            }>
            <Routes>
              <Route path="/" element={<Navigate to="dashboard" />} />
              <Route path="dashboard" element={<Dashboard />} />

              <Route path="home/sections" element={<HomeSections />} />
              <Route path="home/banners" element={<HomeBanners />} />

              <Route
                path="orders"
                element={
                  <Orders
                    orders={orders}
                    orderStats={orderStats}
                    updateOrderStatus={handleUpdateOrderStatus}
                    downloadInvoice={downloadInvoice}
                  />
                }
              />

              <Route path="categories" element={<Categories />} />
              <Route path="brands" element={<Brands />} />
              <Route path="catalog/attributes" element={<ProductAttributes />} />

              <Route path="inventory" element={<InventoryCategory />} />
              <Route path="delivery/pincodes" element={<PincodeDashboard />} />

              <Route path="billing" element={<Billing />} />
              <Route path="purchase" element={<Purchase />} />
              <Route path="purchase-register" element={<PurchaseRegister />} />

              <Route path="reviews" element={<ReviewsDashboard />} />
              <Route path="returns" element={<ReturnRequestsDashboard />} />
              <Route path="notifications" element={<SendNotification />} />
              <Route path="offers" element={<OffersDashboard />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="sellers/dashboard" element={<SellerDashboard />} />
              <Route path="sellers" element={<Sellers />} />
              <Route path="sellers/products" element={<SellerProducts />} />
              <Route path="sellers/pending-edits" element={<SellerPendingEdits />} />
              <Route path="sellers/listings" element={<SellerListings />} />
              <Route path="sellers/payouts" element={<SellerPayouts />} />
              <Route path="merge-duplicates" element={<MergeDuplicates />} />
              <Route path="products/*" element={<ProductsRoutes />} />

              <Route path="settings/payments" element={<PaymentSettings />} />

              <Route path="customers" element={<CustomersDashboard />} />
              <Route path="customers/:id" element={<CustomerDetails />} />
              <Route path="customers/delete-requests" element={<DeleteRequests />} /> {/* ✅ NEW */}

              <Route path="orders/:orderId" element={<AdminOrderDetail />} />

              <Route path="settings/profile-schema" element={<ProfileSchemaSettings />} />
              <Route path="settings/account-sidebar" element={<AccountSidebarSettings />} />
              <Route path="settings/email-alerts" element={<NotificationSettings />} />

              {/* ── Varaii Billing Licenses ── */}
              <Route path="licenses" element={<LicensesDashboard />} />
              <Route path="licenses/new" element={<NewLicense />} />
              <Route path="licenses/:id" element={<LicenseDetail />} />

              {/* ── B2B Wholesale ── */}
              <Route path="b2b/dashboard" element={<B2BDashboard />} />
              <Route path="b2b/buyers" element={<B2BBuyers />} />
              <Route path="b2b/buyers/:id" element={<B2BBuyerDetail />} />
              <Route path="b2b/products" element={<B2BProducts />} />
              <Route path="b2b/orders" element={<B2BOrders />} />
              <Route path="b2b/orders/:id" element={<B2BOrderDetail />} />
              <Route path="b2b/notifications" element={<B2BNotifications />} />
              <Route path="b2b/home" element={<B2BHomeManage />} />

            </Routes>
            </Suspense>
          </div>
        </div>
      </div>
    </AdminCatalogProvider>
  );
}
